import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Printer, Download } from "lucide-react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import api from "@/lib/api";
import NpdQuoteSheet from "@/components/NpdQuoteSheet";

export default function NpdPreview() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const autoPrint = searchParams.get("print") === "1";
  const autoDownload = searchParams.get("download") === "1";
  const [npd, setNpd] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [settings, setSettings] = useState({});
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const quoteRef = useRef(null);
  const didAuto = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const [n, c, s, u] = await Promise.all([
          api.get(`/npd/${id}`),
          api.get("/customers"),
          api.get("/settings/public"),
          api.get("/units"),
        ]);
        setNpd(n.data);
        setCustomers(c.data || []);
        setSettings(s.data || {});
        setUnits(u.data || []);
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "Failed to load NPD";
        setLoadError(msg);
        toast.error(msg);
      } finally { setLoading(false); }
    })();
  }, [id]);

  // Render the on-screen quote to a real PDF file and download it directly (no print dialog).
  // Rasterised via html2canvas → embedded into an A4 jsPDF, sliced across pages as needed.
  const generatePdf = async () => {
    const el = quoteRef.current;
    if (!el || !npd) return;
    setGenerating(true);
    try {
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false });
      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      const pdf = new jsPDF("p", "mm", "a4");
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgW = pageW;
      const imgH = (canvas.height * imgW) / canvas.width;
      let heightLeft = imgH;
      let position = 0;
      pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
      heightLeft -= pageH;
      while (heightLeft > 0) {
        position -= pageH;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
        heightLeft -= pageH;
      }
      const fname = `${npd.npd_number || "NPD"}_${(npd.part_number || "part").replace(/\//g, "-")}.pdf`;
      pdf.save(fname);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Could not generate PDF");
    } finally {
      setGenerating(false);
    }
  };

  // ?download=1 — auto-generate + download once rendered, then try to close the tab.
  useEffect(() => {
    if (loading || !npd || didAuto.current) return;
    if (autoDownload) {
      didAuto.current = true;
      const t = setTimeout(async () => {
        await generatePdf();
        setTimeout(() => { try { window.close(); } catch { /* browser may block */ } }, 500);
      }, 1200); // let the drawing screenshot blob finish loading
      return () => clearTimeout(t);
    }
    if (autoPrint) {
      didAuto.current = true;
      const t = setTimeout(() => window.print(), 1200);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line
  }, [loading, npd, autoDownload, autoPrint]);

  if (loading) return <div className="p-10 text-center text-slate-400">Loading…</div>;
  if (loadError) return (
    <div className="p-10 text-center">
      <div className="text-red-600 font-medium mb-2">Could not load preview</div>
      <div className="text-sm text-slate-500">{loadError}</div>
      <Link to={`/npd/${id}`} className="inline-block mt-4 text-sm text-orange-600 hover:text-orange-700">
        ← Back to NPD
      </Link>
    </div>
  );
  if (!npd) return <div className="p-10 text-center text-slate-400">NPD not found</div>;
  const customerName = customers.find((c) => c.customer_id === npd.customer_id)?.name || "—";
  const unit = units.find((u) => u.unit_id === npd.unit_id) || null;

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between print:hidden">
        <Link to={`/npd/${id}`} className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1.5">
          <ArrowLeft size={16} /> Back to NPD
        </Link>
        <div className="text-sm text-slate-500">Preview · {npd.npd_number}</div>
        <div className="flex items-center gap-2">
          <button
            onClick={generatePdf}
            disabled={generating}
            className="px-4 py-2 border border-slate-300 hover:bg-slate-50 disabled:opacity-60 text-sm font-medium rounded-md flex items-center gap-1.5"
            data-testid="download-pdf-btn"
          >
            <Download size={14} /> {generating ? "Preparing…" : "Download PDF"}
          </button>
          <button
            onClick={() => window.print()}
            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-md flex items-center gap-1.5"
            data-testid="print-btn"
          >
            <Printer size={14} /> Print
          </button>
        </div>
      </div>

      <div className="max-w-[210mm] mx-auto my-6 shadow-md print:shadow-none print:my-0">
        <div ref={quoteRef} className="bg-white p-6 print:p-0">
          <NpdQuoteSheet npd={npd} customerName={customerName} settings={settings} unit={unit} />
        </div>
      </div>

      {(autoDownload || generating) && (
        <div className="fixed inset-0 bg-white/95 z-50 flex flex-col items-center justify-center gap-2 print:hidden" data-testid="pdf-generating-overlay">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          <div className="text-slate-600 text-sm">Preparing PDF… your download will start automatically.</div>
        </div>
      )}

      <style>{`
        @page { size: A4 portrait; margin: 12mm; }
        @media print {
          body { background: white; }
          .print\\:hidden { display: none !important; }
          .quote-sheet { font-size: 11px; }
        }
      `}</style>
    </div>
  );
}
